import { supabase } from '../../services/supabase';
import { scrubDeclinedCharges } from './declinedCharges';

/**
 * Typed wrappers over the creator data sources.
 *
 * Most creator data does not come from tables directly — it comes from the
 * `lunarpay-api` edge function under a named `action`, which is where the
 * service-role joins and money maths live. Screens must go through here rather
 * than inventing their own queries, so there is one place to look when a shape
 * changes and one place that knows which reads are RLS-safe.
 *
 * Every function resolves to a value or throws. Callers should use `useAsync`.
 */

async function invoke<T>(action: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('lunarpay-api', {
    body: { action, ...body },
  });
  if (error) throw error;
  return data as T;
}

/* ── Orders ─────────────────────────────────────────────────────────────── */

export type CreatorOrder = Record<string, any> & {
  id: string;
  status?: string;
  order_type?: string;
  total_amount?: number;
};

export async function getTalentOrders(talentId: string): Promise<CreatorOrder[]> {
  const data = await invoke<{ orders?: CreatorOrder[] }>('get-talent-orders', { talentId });
  return data?.orders ?? [];
}

/* ── Payouts ────────────────────────────────────────────────────────────── */

/**
 * Payout rows and batches, with every fan's declined payment taken out (see
 * `declinedCharges.ts`). `declinedOrderIds` names those orders for screens that
 * count orders.
 */
export async function getTalentPayouts(talentId: string): Promise<any> {
  const raw = await invoke<any>('get-talent-payouts', { talentId });
  return raw ? scrubDeclinedCharges(raw) : raw;
}

/**
 * All-time and current-month sponsor ad earnings.
 *
 * Ad revenue is not part of the payouts payload: it accrues in `sponsor_clicks`
 * and is batched on the 1st, so the dashboard sums it separately. Amounts are
 * stored in cents.
 */
/**
 * PostgREST caps a plain select at 1000 rows and says nothing about it, so a
 * creator past that silently reads as earning less than they did — money, on
 * two screens. One creator is already over (1,434 clicks) and any campaign
 * pushes more of them over.
 *
 * Aggregates are disabled on this project, so there is no `sum()` to ask for;
 * the rows have to be paged and added up here.
 */
async function sumSharePages(build: () => any): Promise<number> {
  const PAGE = 1000;
  let total = 0;
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await build().range(from, from + PAGE - 1);
    if (error) throw error;
    const rows = (data ?? []) as any[];
    total += rows.reduce((t, r) => t + Number(r.creator_share_cents || 0), 0);
    if (rows.length < PAGE) break;
  }
  return total / 100;
}

export async function getAdEarnings(
  talentId: string
): Promise<{ allTime: number; thisMonth: number }> {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();

  const [allTime, thisMonth] = await Promise.all([
    sumSharePages(() =>
      supabase.from('sponsor_clicks').select('creator_share_cents').eq('talent_id', talentId)
    ),
    sumSharePages(() =>
      supabase
        .from('sponsor_clicks')
        .select('creator_share_cents')
        .eq('talent_id', talentId)
        .gte('created_at', monthStart)
        .lt('created_at', monthEnd)
    ),
  ]);

  return { allTime, thisMonth };
}

/* ── Inbox: donations + AMA ─────────────────────────────────────────────── */

export type DonationMessage = Record<string, any> & { id: string; is_read?: boolean };

export async function getDonationMessages(talentId: string): Promise<DonationMessage[]> {
  const data = await invoke<{ messages?: DonationMessage[] }>('get-donation-messages', {
    talentId,
  });
  return data?.messages ?? [];
}

export async function markDonationMessagesRead(talentId: string): Promise<void> {
  await invoke('mark-messages-read', { talentId });
}

/**
 * ask_me_anything_questions has exactly these columns. There is NO `answer`
 * column — questions are anonymous, so "answered" is a state (a timestamp), not
 * stored text. Selecting `answer` makes PostgREST reject the whole request.
 */
export type AmaQuestion = {
  id: string;
  talent_id: string;
  question: string;
  created_at: string;
  answered_at: string | null;
  /** Set when the creator files a question away. NULL = active. */
  archived_at: string | null;
};

export async function getAmaQuestions(talentId: string): Promise<AmaQuestion[]> {
  const { data, error } = await supabase
    .from('ask_me_anything_questions')
    .select('id, talent_id, question, created_at, answered_at, archived_at')
    .eq('talent_id', talentId)
    // Archived questions are filed away, not deleted; callers that want them
    // ask for them explicitly.
    .is('archived_at', null)
    .order('answered_at', { ascending: false, nullsFirst: true })
    .order('created_at', { ascending: false })
    .limit(250);
  if (error) throw error;
  return (data ?? []) as AmaQuestion[];
}

/* ── Fans ───────────────────────────────────────────────────────────────── */

export type Fan = Record<string, any> & { id?: string; email?: string };

export async function getTalentFans(talentId: string): Promise<Fan[]> {
  const data = await invoke<{ fans?: Fan[] }>('get-talent-fans', { talentId });
  return data?.fans ?? [];
}

/* ── Page: bio settings and links ───────────────────────────────────────── */

export async function getBioSettings(talentId: string): Promise<Record<string, any> | null> {
  const { data, error } = await supabase
    .from('bio_settings')
    .select('*')
    .eq('talent_id', talentId)
    .maybeSingle();
  if (error) throw error;
  return (data as any) ?? null;
}

export async function updateBioSettings(
  talentId: string,
  patch: Record<string, any>
): Promise<void> {
  const { error } = await supabase.from('bio_settings').update(patch).eq('talent_id', talentId);
  if (error) throw error;
}

export async function getBioLinks(talentId: string): Promise<Record<string, any>[]> {
  const { data, error } = await supabase
    .from('bio_links')
    .select('*')
    .eq('talent_id', talentId)
    // `display_order`, not `position` — there is no `position` column on
    // bio_links, and ordering by it makes PostgREST 400 the whole request.
    .order('display_order', { ascending: true });
  if (error) throw error;
  return (data ?? []) as any[];
}

/* ── Stats ──────────────────────────────────────────────────────────────── */

export async function getBioPageViewStats(talentId: string): Promise<Record<string, any> | null> {
  const { data, error } = await supabase
    .from('bio_page_view_stats')
    .select('*')
    .eq('talent_id', talentId)
    .maybeSingle();
  if (error) throw error;
  return (data as any) ?? null;
}
